import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Preview as PreviewIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import axios from '../services/axiosConfig';
import EmailEditor from './EmailEditor';
import EmailAttachmentField from './EmailAttachmentField';
import {
  buildAudienceEmailsEndpoint,
  createEmailCampaign,
  validateEmailAttachment,
} from '../utils/emailCampaigns';

const hasEmailContent = (html) => html && html.replace(/<[^>]+>/g, '').trim().length > 0;

const EmailComposer = ({ onCampaignCreated, audienceType, recipientEmails = [] }) => {
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [audienceEmails, setAudienceEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [attachment, setAttachment] = useState(null);

  const urlAudienceType = searchParams.get('audienceType');
  const urlRecipientEmails = searchParams.get('recipientEmails');

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlBody: '',
    audienceType: audienceType || urlAudienceType || '',
    recipientEmails: recipientEmails.length > 0 ? recipientEmails : (urlRecipientEmails ? urlRecipientEmails.split(',') : []),
    scheduledAt: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (formData.audienceType) {
      fetchAudienceEmails(formData.audienceType);
    }
  }, [formData.audienceType]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/email-campaigns/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAudienceEmails = async (audienceType) => {
    try {
      const response = await axios.get(buildAudienceEmailsEndpoint(audienceType));
      setAudienceEmails(response.data.emails);
    } catch (error) {
      console.error('Error fetching audience emails:', error);
      setError('Failed to fetch audience emails');
    }
  };

  const handleLoadTemplate = (template) => {
    setFormData({
      ...formData,
      subject: template.subject || formData.subject,
      htmlBody: template.html_body || '',
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !formData.htmlBody.trim()) {
      setError('Template name and email content are required');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/email-campaigns/templates', {
        name: templateName.trim(),
        subject: formData.subject,
        htmlBody: formData.htmlBody,
      });
      await fetchTemplates();
      setTemplateName('');
      setError(null);
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipient = () => {
    const email = prompt('Enter email address:');
    if (email && email.includes('@')) {
      setFormData({
        ...formData,
        recipientEmails: [...formData.recipientEmails, email.trim()]
      });
    }
  };

  const handleRemoveRecipient = (emailToRemove) => {
    setFormData({
      ...formData,
      recipientEmails: formData.recipientEmails.filter(email => email !== emailToRemove)
    });
  };

  const handleSendCampaign = async (schedule = false) => {
    try {
      setLoading(true);

      const campaignData = {
        name: formData.name,
        subject: formData.subject,
        htmlBody: formData.htmlBody,
        audienceType: formData.audienceType,
        recipientEmails: formData.recipientEmails,
        scheduledAt: schedule ? formData.scheduledAt : null,
        saveAsDraft: schedule,
      };

      const response = await createEmailCampaign(axios, campaignData, attachment);

      if (onCampaignCreated) {
        onCampaignCreated(response.data.campaign);
      }

      setFormData({
        name: '',
        subject: '',
        htmlBody: '',
        audienceType: audienceType || '',
        recipientEmails: recipientEmails,
        scheduledAt: ''
      });
      setAttachment(null);

      setError(null);
      alert(schedule ? 'Campaign scheduled successfully!' : 'Campaign sent successfully!');
    } catch (error) {
      console.error('Error creating campaign:', error);
      setError(error.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.name && formData.subject && hasEmailContent(formData.htmlBody) && formData.audienceType && formData.recipientEmails.length > 0;

  if (loading && templates.length === 0 && !formData.htmlBody) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Compose Email Campaign
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Campaign Details
              </Typography>

              <TextField
                fullWidth
                label="Campaign Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Subject Line"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                margin="normal"
                required
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Audience Type</InputLabel>
                <Select
                  value={formData.audienceType}
                  onChange={(e) => setFormData({ ...formData, audienceType: e.target.value })}
                  required
                >
                  <MenuItem value="first_timers">First Timers</MenuItem>
                  <MenuItem value="new_converts">New Converts</MenuItem>
                  <MenuItem value="harvest_contacts">Harvest Contacts</MenuItem>
                  <MenuItem value="all">All Contacts</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Recipients
              </Typography>

              {formData.audienceType && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Available emails for {formData.audienceType.replace('_', ' ')}: {audienceEmails.length}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const allEmails = audienceEmails.map(item => item.email);
                        setFormData({ ...formData, recipientEmails: allEmails });
                      }}
                    >
                      Select All ({audienceEmails.length})
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setFormData({ ...formData, recipientEmails: [] })}
                    >
                      Clear All
                    </Button>
                  </Box>

                  <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {audienceEmails.slice(0, 20).map((item, index) => (
                      <ListItem key={index} dense>
                        <ListItemText
                          primary={item.email}
                          secondary={`${item.first_name} ${item.last_name}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => {
                              if (formData.recipientEmails.includes(item.email)) {
                                handleRemoveRecipient(item.email);
                              } else {
                                setFormData({
                                  ...formData,
                                  recipientEmails: [...formData.recipientEmails, item.email]
                                });
                              }
                            }}
                            size="small"
                            color={formData.recipientEmails.includes(item.email) ? 'error' : 'primary'}
                          >
                            {formData.recipientEmails.includes(item.email) ? <DeleteIcon /> : <AddIcon />}
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddRecipient} size="small">
                  Add Custom Email
                </Button>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Selected Recipients ({formData.recipientEmails.length})
              </Typography>

              <List dense>
                {formData.recipientEmails.map((email, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={email} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveRecipient(email)} size="small">
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <TextField
                fullWidth
                label="Schedule Campaign (optional)"
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              {templates.length > 0 && (
                <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
                  <InputLabel>Load Saved Template</InputLabel>
                  <Select
                    value=""
                    displayEmpty
                    onChange={(e) => {
                      const template = templates.find((item) => String(item.id) === e.target.value);
                      if (template) handleLoadTemplate(template);
                    }}
                  >
                    <MenuItem value="" disabled>Select a template</MenuItem>
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={String(template.id)}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <EmailEditor
                value={formData.htmlBody}
                onChange={(htmlBody) => setFormData({ ...formData, htmlBody })}
                minHeight={280}
              />

              <EmailAttachmentField
                attachment={attachment}
                onChange={(file) => {
                  const attachmentError = validateEmailAttachment(file);
                  setError(attachmentError);
                  setAttachment(attachmentError ? null : file);
                }}
              />

              <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
                <TextField
                  label="Save as template"
                  placeholder="Template name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" onClick={handleSaveTemplate} disabled={!templateName.trim() || !formData.htmlBody.trim()}>
                  Save
                </Button>
                <Button variant="text" startIcon={<PreviewIcon />} onClick={() => setPreviewDialogOpen(true)} disabled={!formData.htmlBody.trim()}>
                  Preview
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<ScheduleIcon />}
          onClick={() => handleSendCampaign(true)}
          disabled={!isFormValid || !formData.scheduledAt}
        >
          Schedule Campaign
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => handleSendCampaign(false)}
          disabled={!isFormValid}
        >
          Send Now
        </Button>
      </Box>

      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Subject: {formData.subject || '(no subject)'}
          </Typography>
          <Box
            sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            dangerouslySetInnerHTML={{ __html: formData.htmlBody }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailComposer;
